import { Loader2, Trash2 } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { toast } from 'sonner';

import { useDeleteLineItemMutation } from '~entities/student/api/payments/delete-line-item';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';

interface CancelLineItemProps {
  transactionId: string;
  type: string;
}

export const DeleteLineItem = forwardRef<
  HTMLButtonElement,
  CancelLineItemProps
>(({ transactionId, type }, ref) => {
  const [isOpen, setIsOpen] = useState(false);

  const { mutateAsync: deleteLineItem, isPending } =
    useDeleteLineItemMutation();

  const handleDeleteLineItem = async () => {
    try {
      await deleteLineItem({ invoiceId: transactionId, type });
      toast.success('Line item deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete line item');
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size='icon'
          variant='ghost'
          className='ml-1 size-6 opacity-70 hover:opacity-100'
          ref={ref}
        >
          <Trash2 className='size-3.5 text-red-500' />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Line Item</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this line item? This action cannot
            be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <Button onClick={handleDeleteLineItem} disabled={isPending}>
            {isPending ? (
              <Loader2 className='size-4 animate-spin' />
            ) : (
              'Continue'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});

DeleteLineItem.displayName = 'DeleteLineItem';
